import { t } from 'i18n';
import React, { useState, useEffect, ReactNode } from 'react';
import {
  Container,
  styled,
  Button,
  Card,
  Link,
  TooltipUncontrolled,
  SvgIcon,
  CardProps,
} from '@teko/ui';
import { Flex, Box } from 'reflexbox';
import { space } from 'styled-system';
import { useRouter } from 'next/router';
import { EmptyCartPanel, Breadcrumb, ListTile } from '@teko/ecommerce';
import { CautionOutlined } from '@teko/ds-icon';
import { Loading } from '../../../components/Loading';
import { CartContextState, useGlobalData, useTenantConfig } from 'contexts';
import { usePaymentMethods } from 'hooks';
import { useAuth } from '@teko/auth-react';
import {
  getSelectedCartPromo,
  getTotalCartDiscount,
  getCartTitle,
} from 'helpers';
import { ORDER_SESSION_STORAGE_KEY } from '../../../constants';

import dynamic from 'next/dynamic';

const MiniDropdown = dynamic(() =>
  import('@teko/ecommerce').then((mod) => mod.MiniDropdown),
);

const PartOrderSelection = dynamic(() =>
  import('../organisms/PartOrderSelection').then(
    (mod) => mod.PartOrderSelection,
  ),
);

const CartPromotionBlock = dynamic(() =>
  import('components/CartPromotionSelected').then(
    (mod) => mod.CartPromotionBlock,
  ),
);

const CartDiscount = dynamic(() =>
  import('../organisms/CartDiscount').then((mod) => mod.CartDiscount),
);

const Invoices = dynamic(() =>
  import('@teko/ecommerce').then((mod) => mod.Invoices),
);

const Modal = dynamic(() => import('@teko/ui').then((mod) => mod.Modal));

const ModalBody = dynamic(() =>
  import('@teko/ui').then((mod) => mod.ModalBody),
);

const ModalHeader = dynamic(() =>
  import('@teko/ui').then((mod) => mod.ModalHeader),
);

const ModalFooter = dynamic(() =>
  import('@teko/ui').then((mod) => mod.ModalFooter),
);

interface CartProps {
  cart: CartContextState;
  cartLineList: ReactNode;
  withoutPayment?: boolean;
  allowExportPrice?: boolean;
  pricePDFTemplate?: ReactNode;
}
interface StyledCardProps extends CardProps {
  firstDom: boolean;
}
const StyledContainer = styled(Container)`
  min-height: 70vh;
  @media print {
    display: none;
  }
`;
const StyledEmptyCartPanel = styled(EmptyCartPanel)`
  width: 400px;
`;
const EmptyWrapper = styled(Card)`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const StyledCard = styled(Card)<StyledCardProps>`
  padding: 1rem;
  margin-top: ${(p) => (p.firstDom ? '0' : '1rem')};
`;
const PaymentButton = styled(Button)`
  padding: 0.7rem 1rem;
  margin-top: 1rem;
`;
const PaymentLoginButton = styled(Button)`
  padding: 0.4rem 1rem;
  margin-top: 1rem;
  display: flex;
  flex-direction: column;

  .help {
    font-size: 12px;
  }
`;
const Title = styled.div`
  font-size: 24px;
  font-weight: bold;
  color: #333333;
`;
const ClearCart = styled(Button)`
  &:hover {
    color: red;
  }
`;
const FlexSpaceBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const StyledButton = styled(Button)`
  height: 36px;
  padding: 0 20px;
  min-width: 100px;
`;
const CartPromotionWrapper = styled.div`
  padding-top: 1rem;
`;
const CartPromotionContent = styled.div`
  margin: 12px 0;
  background-color: white;
`;
const AlertInfo = styled.div(
  space,
  () => `
    background-color: #f6f6f6;
    padding: 1rem;
    border-radius: 8px;
  `,
);
const ToolTipWrapper = styled.span`
  margin-left: 4px;
  margin-top: -2px;
`;
const Print = styled.div`
  display: none;
  @media print {
    position: absolute;
    top: 0;
    background-color: white;
    display: block;
    z-index: 100001; // larger than pushdy css
    width: 100%;
    min-height: 100vh;
    overflow-y: auto;
  }
`;
const DropdownWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
const StyledListTile = styled(ListTile)`
  padding: 0.4rem 0.6rem;
  font-size: 0.875rem;
  text-align: left;
`;
const promotionTitle = t('Khuyến mãi của bạn');
export const Cart: React.FunctionComponent<CartProps> = ({
  cart,
  cartLineList,
  withoutPayment = false,
  allowExportPrice = false,
  pricePDFTemplate,
}: CartProps) => {
  const globalData = useGlobalData();
  const router = useRouter();
  const isMasan = globalData.tenant === 'masan-dealer';
  const { user, authService } = useAuth();
  const [showPopup, setShowPopup] = useState(false);
  const { paymentDeposit, resetPaymentMethods } = withoutPayment
    ? {
        paymentDeposit: undefined,
        resetPaymentMethods: () => {},
      }
    : usePaymentMethods();
  const { hideCouponSection } = useTenantConfig();

  const shippingFee = Math.max(
    cart?.order?.shippingFee - cart?.order?.shippingFeeDiscount || 0,
    0,
  );

  useEffect(() => {
    if (cart.order?.grandTotal !== undefined && user) {
      resetPaymentMethods();
    }
  }, [cart.order?.grandTotal]);

  useEffect(() => {
    // re-payment
    // this uses for clear previous order in sessionStorage if it exists
    sessionStorage.removeItem(ORDER_SESSION_STORAGE_KEY);
  }, []);

  const selectedCartPromotion = getSelectedCartPromo(cart?.order?.promotions);
  const totalCartDiscount = getTotalCartDiscount(cart);
  const getCartContent = (): React.ReactNode => {
    if (!cart.loading && cart.isEmpty) {
      return (
        <EmptyWrapper>
          <StyledEmptyCartPanel />
        </EmptyWrapper>
      );
    }
    if (!!cart?.order?.orderLines?.length) {
      // sumUpTotal and remainPayment calculate separately for cloned orders.
      // Because cloned order case can make totalPaid greater than grandTotal
      // and in this case, remainPayment returned by cart api equals 0,
      // it's needed to recalculate sumUpTotal and remainPayment.
      const fallbackSumUpTotal =
        Math.max(cart.order?.grandTotal - cart.order?.eVouchersDiscount, 0) ||
        0;
      const fallbackRemainPayment =
        fallbackSumUpTotal - (cart.order?.totalPaid || 0);

      const subTotalDisplay =
        cart?.subTotalAfterLineDiscount - cart?.totalPriceDifferences;
      return (
        <>
          <Flex marginBottom={16}>
            <Box width="67%" marginRight={16}>
              <FlexSpaceBetween>
                <Title>
                  {getCartTitle(cart?.order?.orderId, cart?.order?.isClone)}
                </Title>
                <ClearCart
                  link
                  variant="secondary"
                  onClick={(): void => setShowPopup(true)}
                >
                  {t('Xóa tất cả')}
                </ClearCart>
              </FlexSpaceBetween>
            </Box>
            {allowExportPrice && (
              <Box width="33%">
                <DropdownWrapper>
                  <MiniDropdown text={t('Tải báo giá')}>
                    <StyledListTile
                      leading={<SvgIcon name="pdf" size={16} />}
                      title="PDF"
                      onClick={(): void => window.print()}
                    />
                  </MiniDropdown>
                </DropdownWrapper>
              </Box>
            )}
          </Flex>
          <Flex>
            <Box width="67%" marginRight={16}>
              {cartLineList}
              {!!cart.partOrderPromotions.length && (
                <PartOrderSelection
                  selectedPromotion={cart.selectedPartOrderPromotion}
                  partOrderPromotions={cart.partOrderPromotions}
                  onApplyPromotion={cart.confirmPartOrderPromotion}
                  onRemovePromotion={cart.onRemoveCoupon}
                  sellerLines={cart?.order?.sellerLines}
                  giftsImgMap={cart?.giftsImgMap}
                  onBuyMore={() => {
                    router.push('/');
                  }}
                />
              )}
              {!!selectedCartPromotion && (
                <CartPromotionWrapper>
                  <Title>{promotionTitle}</Title>
                  <CartPromotionContent>
                    <CartPromotionBlock
                      selectedPromotion={selectedCartPromotion}
                      sellerLines={cart?.order?.sellerLines}
                      giftsImgMap={cart?.giftsImgMap}
                    />
                  </CartPromotionContent>
                </CartPromotionWrapper>
              )}
            </Box>
            <Box width="33%">
              {!hideCouponSection && <CartDiscount cart={cart} />}
              <StyledCard
                firstDom={isMasan}
                header={<h5>{t('Thanh toán')}</h5>}
                footer={
                  <>
                    {paymentDeposit?.allowDeposit && (
                      <AlertInfo mt={3}>
                        {t('Đơn hàng được phép trả trước')}
                        <ToolTipWrapper>
                          <TooltipUncontrolled
                            as="span"
                            placement="left"
                            trigger={<CautionOutlined />}
                            content={t(
                              'sites.shop-front.components.desktop.pages.Cart.0',
                            )}
                          />
                        </ToolTipWrapper>
                      </AlertInfo>
                    )}
                    {user ? (
                      <Link href="/checkout">
                        <PaymentButton
                          variant="primary"
                          block
                          data-track-content
                          data-content-region-name="bottomShoppingCart"
                          data-content-name="createOrder"
                          disabled={cart?.order?.orderLines.every(
                            (item) => !item.isSelected,
                          )}
                        >
                          {isMasan ? t('TIẾN HÀNH ĐẶT HÀNG') : t('TIẾP TỤC')}
                        </PaymentButton>
                      </Link>
                    ) : (
                      <PaymentLoginButton
                        variant="primary"
                        block
                        onClick={(): void => {
                          authService.login(
                            `${window.location.origin}/checkout`,
                            false,
                          );
                        }}
                      >
                        {t('THANH TOÁN')}
                        <span className="help">
                          {t('Bạn cần đăng nhập để tiếp tục')}
                        </span>
                      </PaymentLoginButton>
                    )}
                  </>
                }
              >
                <Invoices
                  vat={true}
                  align="left"
                  textColor="#848788"
                  invoices={[
                    {
                      text: t('Tạm tính'),
                      price: subTotalDisplay,
                    },
                    shippingFee > 0 && {
                      text: t('Phí vận chuyển'),
                      price: shippingFee,
                    },
                    !!cart.installService?.fee && {
                      text: t('Phí hỗ trợ kỹ thuật'),
                      price: cart.installService.fee,
                    },
                    ...cart.appliedCoupons.map((coupon) => ({
                      text: t('Mã giảm giá'),
                      tag: coupon.condition.coupon,
                      price: -coupon.amount,
                    })),
                    ...cart.appliedEVouchers.map((eVoucher) => ({
                      text: t('Phiếu mua hàng'),
                      tag: eVoucher.condition.coupon,
                      price: -(eVoucher.appliedAmount || eVoucher.amount),
                    })),
                    cart.totalPartOrderDiscount > 0 && {
                      text: cart.partOrderDiscountText,
                      price: -cart.totalPartOrderDiscount,
                    },
                    totalCartDiscount > 0 && {
                      text: t('Khuyến mại đơn hàng'),
                      price: -totalCartDiscount,
                    },
                    cart?.totalPriceDifferences > 0 && {
                      text: t('Tổng nâng giá'),
                      price: cart.totalPriceDifferences,
                    },
                    {
                      text: t('Thành tiền'),
                      price:
                        cart?.order?.remainPayment > 0
                          ? cart?.order?.remainPayment + cart?.order?.totalPaid
                          : fallbackSumUpTotal,
                    },
                    cart?.order?.totalPaid > 0 && {
                      separator: true,
                      text: t('Đã thanh toán'),
                      price: cart.order.totalPaid,
                    },
                    cart?.order?.totalPaid > 0 && {
                      text:
                        (cart?.order?.remainPayment || fallbackRemainPayment) >=
                        0
                          ? t('Còn phải thanh toán')
                          : t('Số tiền thừa của khách'),
                      price: Math.abs(
                        cart?.order?.remainPayment || fallbackRemainPayment,
                      ),
                    },
                  ].filter((invoice) => !!invoice)}
                />
              </StyledCard>
              <div className="cartInlineBox" />
            </Box>
          </Flex>
        </>
      );
    }
  };
  return (
    <Loading isLoading={cart.loading || cart.cartDiscountLoading}>
      {!cart.isEmpty &&
        !!cart?.order?.orderLines?.length &&
        !!allowExportPrice && <Print>{pricePDFTemplate}</Print>}
      <StyledContainer marginTop={16} marginBottom={16}>
        <Breadcrumb
          marginBottom={16}
          items={[
            {
              disabled: false,
              text: '',
              href: '/',
              icon: {
                name: 'home',
                size: 16,
              },
            },
            {
              disabled: true,
              text: t('Giỏ hàng'),
            },
          ]}
          divider=">"
          outline
        />
        {getCartContent()}
      </StyledContainer>
      <Modal
        show={showPopup}
        onHide={(): void => setShowPopup(false)}
        size="sm"
      >
        <ModalHeader>{t('Chú ý')}</ModalHeader>
        <ModalBody>
          {t('sites.shop-front.components.desktop.pages.Cart.1')}
        </ModalBody>
        <ModalFooter>
          <StyledButton onClick={(): void => setShowPopup(false)}>
            {t('Hủy bỏ')}
          </StyledButton>
          <StyledButton
            marginLeft={16}
            variant="primary"
            onClick={(): Promise<void> =>
              cart.onDeleteCart().then(() => setShowPopup(false))
            }
          >
            {t('Đồng ý')}
          </StyledButton>
        </ModalFooter>
      </Modal>
    </Loading>
  );
};
export const TRACKING_PAGE_VIEWS = {
  screenName: 'shoppingCart',
  contentType: 'checkout',
};
